import { Data, Effect, Option, Schema } from 'effect';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { okraApi } from '../okraApi';

export type HostedPdfAccessMode = 'public' | 'unlisted' | 'disabled';

export type HostedPdfUrls = {
  view: string;
  pdf: string | null;
  download: string | null;
  embed: string | null;
  manifest: string | null;
  thumbnail: string | null;
  page: string | null;
  html: string | null;
  text: string | null;
  data: string | null;
};

export type HostedPdf = {
  id: string;
  namespace: string;
  hostname: string | null;
  documentId: string | null;
  title: string;
  fileName: string;
  mime: string;
  size: number;
  sha256: string | null;
  accessMode: HostedPdfAccessMode;
  expiresAt: string | null;
  expiredUrl: string | null;
  allowDownload: boolean;
  doIndex: boolean;
  passwordRequired: boolean;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  deploymentName: string | null;
  analytics: {
    views: number;
    lastViewedAt: string | null;
  };
  urls: HostedPdfUrls;
  viewUrl: string;
  pdfUrl: string | null;
  thumbnailUrl: string | null;
};

export type UpdateHostedPdfInput = {
  namespace: string;
  /** Absent keeps the title; the API keeps it for an empty one too. */
  title?: string;
  access: {
    mode: HostedPdfAccessMode;
    password?: string | null;
    allowDownload: boolean;
    doIndex: boolean;
    /** ISO time the link stops opening; null clears it, absent keeps it. */
    expiresAt?: string | null;
    /** Where an expired link sends visitors; null clears it, absent keeps it. */
    expiredUrl?: string | null;
  };
};

export type CreateHostedPdfInput = {
  documentId: string;
  title?: string;
  /**
   * Who can open the new link (W.4.124 R5, Create link from Documents).
   * Absent: unlisted with download on, the Documents panel's quick link.
   */
  access?: {
    mode: 'unlisted' | 'public' | 'password';
    allowDownload: boolean;
    /** Required when `mode` is `password`; never stored by the client. */
    password?: string;
  };
};

export type HostedLinkHealth = {
  state: 'live' | 'dead' | 'unknown';
  status: number | null;
};

const nonEmptyString = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter((value) => value.trim().length > 0 || 'Expected a non-whitespace string'),
);
const nullableString = Schema.NullOr(Schema.String);
const optionalNullableString = Schema.optionalKey(nullableString);
const optionalFinite = Schema.optionalKey(Schema.Finite);

const hostedPdfUrlsWireSchema = Schema.StructWithRest(
  Schema.Struct({
    view: nonEmptyString,
    pdf: optionalNullableString,
    download: optionalNullableString,
    embed: optionalNullableString,
    manifest: optionalNullableString,
    thumbnail: optionalNullableString,
    page: optionalNullableString,
    html: optionalNullableString,
    text: optionalNullableString,
    data: optionalNullableString,
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const hostedPdfAccessWireSchema = Schema.StructWithRest(
  Schema.Struct({
    mode: Schema.optionalKey(Schema.Literals(['public', 'unlisted', 'disabled'])),
    expires_at: optionalNullableString,
    expired_url: optionalNullableString,
    allow_download: Schema.optionalKey(Schema.Boolean),
    do_index: Schema.optionalKey(Schema.Boolean),
    password_required: Schema.optionalKey(Schema.Boolean),
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const hostedPdfDeploymentWireSchema = Schema.StructWithRest(
  Schema.Struct({ name: optionalNullableString }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const hostedPdfAnalyticsWireSchema = Schema.StructWithRest(
  Schema.Struct({
    views: Schema.optionalKey(Schema.Finite),
    last_viewed_at: optionalNullableString,
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const hostedPdfWireSchema = Schema.StructWithRest(
  Schema.Struct({
    id: nonEmptyString,
    namespace: nonEmptyString,
    hostname: optionalNullableString,
    document_id: optionalNullableString,
    file_id: optionalNullableString,
    title: optionalNullableString,
    file_name: optionalNullableString,
    mime: optionalNullableString,
    size: optionalFinite,
    bytes: optionalFinite,
    sha256: optionalNullableString,
    access: Schema.optionalKey(Schema.NullOr(hostedPdfAccessWireSchema)),
    published_at: optionalNullableString,
    created_at: optionalNullableString,
    updated_at: optionalNullableString,
    deployment: Schema.optionalKey(Schema.NullOr(hostedPdfDeploymentWireSchema)),
    analytics: Schema.optionalKey(Schema.NullOr(hostedPdfAnalyticsWireSchema)),
    views: optionalFinite,
    last_viewed_at: optionalNullableString,
    urls: hostedPdfUrlsWireSchema,
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const hostedPdfListWireSchema = Schema.Union([
  Schema.Array(Schema.Unknown),
  Schema.StructWithRest(
    Schema.Struct({
      data: Schema.Array(Schema.Unknown),
      has_more: Schema.optionalKey(Schema.Boolean),
      next_cursor: optionalNullableString,
    }),
    [Schema.Record(Schema.String, Schema.Unknown)],
  ),
]);

const hostedPdfAnalyticsSeriesWireSchema = Schema.StructWithRest(
  Schema.Struct({
    namespace: Schema.String,
    window_days: optionalFinite,
    total_views: optionalFinite,
    last_viewed_at: optionalNullableString,
    days: Schema.Array(
      Schema.StructWithRest(Schema.Struct({ day: Schema.String, views: Schema.Finite }), [
        Schema.Record(Schema.String, Schema.Unknown),
      ]),
    ),
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const decodeHostedPdfAnalyticsSeriesWire = Schema.decodeUnknownOption(
  hostedPdfAnalyticsSeriesWireSchema,
);

const decodeHostedPdfWireEffect = Schema.decodeUnknownEffect(hostedPdfWireSchema);
const decodeHostedPdfListWireEffect = Schema.decodeUnknownEffect(hostedPdfListWireSchema);

export type HostedPdfDecodeOperation = 'record' | 'list';

/** Safe decode failure: publication payloads and parser diagnostics stay out of serializable metadata. */
export class HostedPdfDecodeError extends Data.TaggedError('HostedPdfDecodeError')<{
  readonly operation: HostedPdfDecodeOperation;
  readonly message: string;
}> {}

const hostedPdfDecodeErrorCauses = new WeakMap<HostedPdfDecodeError, unknown>();

function hostedPdfDecodeFailure(
  operation: HostedPdfDecodeOperation,
  cause: unknown,
): HostedPdfDecodeError {
  const error = new HostedPdfDecodeError({
    operation,
    message:
      operation === 'record'
        ? 'Unable to decode the hosted PDF publication.'
        : 'Unable to decode the hosted PDF list.',
  });
  hostedPdfDecodeErrorCauses.set(error, cause);
  return error;
}

export function hostedPdfDecodeErrorCause(error: HostedPdfDecodeError): unknown {
  return hostedPdfDecodeErrorCauses.get(error) ?? error;
}

function optionalText(value: string | null | undefined): string | null {
  return value?.trim() ? value : null;
}

export type HostedPdfViewDay = { day: string; views: number };

export type HostedPdfAnalyticsSeries = {
  namespace: string;
  windowDays: number;
  totalViews: number;
  lastViewedAt: string | null;
  days: HostedPdfViewDay[];
};

export function parseHostedPdfAnalyticsSeries(value: unknown): HostedPdfAnalyticsSeries | null {
  const decoded = decodeHostedPdfAnalyticsSeriesWire(value);
  if (Option.isNone(decoded)) return null;
  const body = decoded.value;
  const days = body.days.map((entry) => ({
    day: entry.day,
    views: Math.max(0, Math.trunc(entry.views)),
  }));
  return {
    namespace: body.namespace,
    windowDays: Math.max(1, Math.trunc(body.window_days ?? days.length)),
    totalViews: Math.max(0, Math.trunc(body.total_views ?? 0)),
    lastViewedAt: optionalText(body.last_viewed_at),
    days,
  };
}

type HostedPdfWire = typeof hostedPdfWireSchema.Type;

function hostedPdfFromWire(row: HostedPdfWire): HostedPdf {
  const access = row.access ?? undefined;
  const deployment = row.deployment ?? undefined;
  const analytics = row.analytics ?? undefined;
  const createdAt = optionalText(row.created_at) ?? '';
  const publishedAt = optionalText(row.published_at) ?? createdAt;
  const title = optionalText(row.title) ?? optionalText(row.file_name) ?? row.namespace;
  const fileName = optionalText(row.file_name) ?? `${row.namespace}.pdf`;
  return {
    id: row.id,
    namespace: row.namespace,
    hostname: optionalText(row.hostname),
    documentId: optionalText(row.document_id) ?? optionalText(row.file_id),
    title,
    fileName,
    mime: optionalText(row.mime) ?? 'application/pdf',
    size: row.size ?? row.bytes ?? 0,
    sha256: optionalText(row.sha256),
    accessMode: access?.mode ?? 'public',
    expiresAt: optionalText(access?.expires_at),
    expiredUrl: optionalText(access?.expired_url),
    allowDownload: access?.allow_download ?? true,
    doIndex: access?.do_index ?? false,
    passwordRequired: access?.password_required === true,
    publishedAt,
    createdAt,
    updatedAt: optionalText(row.updated_at) ?? publishedAt,
    deploymentName: optionalText(deployment?.name),
    analytics: {
      views: Math.max(0, Math.trunc(analytics?.views ?? row.views ?? 0)),
      lastViewedAt: optionalText(analytics?.last_viewed_at) ?? optionalText(row.last_viewed_at),
    },
    urls: {
      view: row.urls.view,
      pdf: optionalText(row.urls.pdf),
      download: optionalText(row.urls.download),
      embed: optionalText(row.urls.embed),
      manifest: optionalText(row.urls.manifest),
      thumbnail: optionalText(row.urls.thumbnail),
      page: optionalText(row.urls.page),
      html: optionalText(row.urls.html),
      text: optionalText(row.urls.text),
      data: optionalText(row.urls.data),
    },
    viewUrl: row.urls.view,
    pdfUrl: optionalText(row.urls.pdf),
    thumbnailUrl: optionalText(row.urls.thumbnail),
  };
}

export const parseHostedPdfEffect = Effect.fn('hostedPdf.decode')((value: unknown) =>
  decodeHostedPdfWireEffect(value).pipe(
    Effect.map(hostedPdfFromWire),
    Effect.mapError((cause) => hostedPdfDecodeFailure('record', cause)),
  ),
);

export function parseHostedPdf(value: unknown): HostedPdf | null {
  return Option.getOrNull(Effect.runSync(Effect.option(parseHostedPdfEffect(value))));
}

type HostedPdfListPage = {
  data: HostedPdf[];
  hasMore: boolean | null;
  nextCursor: string | null;
};

const parseHostedPdfListPageEffect = Effect.fn('hostedPdf.decodeListPage')(function* (
  value: unknown,
) {
  const decoded = yield* decodeHostedPdfListWireEffect(value).pipe(
    Effect.mapError(() =>
      hostedPdfDecodeFailure(
        'list',
        new Error('The hosted PDF list returned an invalid response.'),
      ),
    ),
  );
  const rows: readonly unknown[] = 'data' in decoded ? decoded.data : decoded;
  const data = yield* Effect.forEach(rows, (row) =>
    parseHostedPdfEffect(row).pipe(
      Effect.mapError(() =>
        hostedPdfDecodeFailure(
          'list',
          new Error('The hosted PDF list contains an invalid publication record.'),
        ),
      ),
    ),
  );
  if (!('data' in decoded)) return { data, hasMore: null, nextCursor: null };
  return {
    data,
    hasMore: decoded.has_more ?? null,
    nextCursor: optionalText(decoded.next_cursor),
  };
});

function parseHostedPdfListPage(value: unknown): HostedPdfListPage {
  return Effect.runSync(
    parseHostedPdfListPageEffect(value).pipe(
      Effect.catch((error) => Effect.fail(hostedPdfDecodeErrorCause(error))),
    ),
  );
}

export const parseHostedPdfListEffect = Effect.fn('hostedPdf.decodeList')(function* (
  value: unknown,
) {
  return (yield* parseHostedPdfListPageEffect(value)).data;
});

export function parseHostedPdfList(value: unknown): HostedPdf[] {
  return Effect.runSync(
    parseHostedPdfListEffect(value).pipe(
      Effect.catch((error) => Effect.fail(hostedPdfDecodeErrorCause(error))),
    ),
  );
}

function hostedPdfListError(message: string, data?: unknown): FetchBaseQueryError {
  return { status: 'CUSTOM_ERROR', error: message, data };
}

export const hostEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listHostedPdfs: build.query<HostedPdf[], { limit?: number } | void>({
      async queryFn(params, _api, _extraOptions, fetchWithBQ) {
        const pageSize = Math.min(100, Math.max(1, params?.limit ?? 20));
        const result = await fetchWithBQ(`/v1/host?limit=${pageSize}`);
        if (result.error) return { error: result.error };
        try {
          return { data: parseHostedPdfListPage(result.data).data };
        } catch (error) {
          return {
            error: hostedPdfListError(
              error instanceof Error ? error.message : 'The hosted PDF list could not be decoded.',
              result.data,
            ),
          };
        }
      },
      providesTags: ['HostedPdfs'],
    }),
    listAllHostedPdfs: build.query<HostedPdf[], { pageSize?: number } | void>({
      async queryFn(params, _api, _extraOptions, fetchWithBQ) {
        const pageSize = Math.min(100, Math.max(1, params?.pageSize ?? 100));
        const publications: HostedPdf[] = [];
        const seenIds = new Set<string>();
        const seenCursors = new Set<string>();
        let cursor: string | null = null;

        while (true) {
          const search = new URLSearchParams({ limit: String(pageSize) });
          if (cursor) search.set('cursor', cursor);
          const result = await fetchWithBQ(`/v1/host?${search.toString()}`);
          if (result.error) return { error: result.error };

          let page: HostedPdfListPage;
          try {
            page = parseHostedPdfListPage(result.data);
          } catch (error) {
            return {
              error: hostedPdfListError(
                error instanceof Error
                  ? error.message
                  : 'The hosted PDF list could not be decoded.',
                result.data,
              ),
            };
          }

          for (const publication of page.data) {
            if (seenIds.has(publication.id)) {
              return {
                error: hostedPdfListError(
                  'Hosted PDF pagination repeated a publication, so sharing state may be incomplete.',
                  { hosted_pdf_id: publication.id },
                ),
              };
            }
            seenIds.add(publication.id);
            publications.push(publication);
          }

          if (page.hasMore === null) {
            if (page.data.length >= pageSize) {
              return {
                error: hostedPdfListError(
                  'The hosted PDF service did not provide pagination metadata, so sharing state may be incomplete.',
                ),
              };
            }
            break;
          }
          if (!page.hasMore) break;
          if (page.nextCursor === null || seenCursors.has(page.nextCursor)) {
            return {
              error: hostedPdfListError(
                'Hosted PDF pagination did not return a usable next cursor.',
              ),
            };
          }
          seenCursors.add(page.nextCursor);
          cursor = page.nextCursor;
        }

        return { data: publications };
      },
      providesTags: ['HostedPdfs'],
    }),
    createHostedPdf: build.mutation<HostedPdf, CreateHostedPdfInput>({
      query: ({ documentId, title, access }) => ({
        url: '/v1/host',
        method: 'POST',
        body: {
          document_id: documentId,
          ...(title?.trim() ? { title: title.trim() } : {}),
          // A password link is unlisted with a password, the API's shape (the runner's too).
          access: {
            mode: access?.mode === 'public' ? 'public' : 'unlisted',
            allow_download: access?.allowDownload ?? true,
            do_index: false,
            ...(access?.mode === 'password' ? { password: access.password ?? '' } : {}),
          },
        },
      }),
      transformResponse: (body: unknown) => {
        const parsed = parseHostedPdf(body);
        if (!parsed) throw new Error('The hosted PDF create request returned an invalid response.');
        return parsed;
      },
      invalidatesTags: ['HostedPdfs'],
    }),
    getHostedPdfAnalytics: build.query<
      HostedPdfAnalyticsSeries,
      { namespace: string; days?: number }
    >({
      query: ({ namespace, days }) => {
        const windowDays = Math.min(90, Math.max(7, Math.trunc(days ?? 30)));
        return `/v1/host/${encodeURIComponent(namespace)}/analytics?days=${windowDays}`;
      },
      transformResponse: (body: unknown) => {
        const parsed = parseHostedPdfAnalyticsSeries(body);
        if (!parsed) throw new Error('The hosted PDF analytics response could not be decoded.');
        return parsed;
      },
      providesTags: ['HostedPdfs'],
    }),
    checkHostedLink: build.query<HostedLinkHealth, string>({
      // Probes the public URL from the browser. The delivery worker answers
      // HEAD without a view count (W.4.75) and sends
      // Access-Control-Allow-Origin: *, so the true status is readable.
      // 401 means the password gate answered, so the link is alive.
      async queryFn(url) {
        try {
          const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
          const state = response.ok || response.status === 401 ? 'live' : 'dead';
          return { data: { state, status: response.status } };
        } catch {
          return { data: { state: 'unknown', status: null } };
        }
      },
    }),
    updateHostedPdf: build.mutation<HostedPdf, UpdateHostedPdfInput>({
      query: ({ namespace, title, access }) => ({
        url: `/v1/host/${encodeURIComponent(namespace)}`,
        method: 'PATCH',
        body: {
          ...(title !== undefined ? { title } : {}),
          access: {
            mode: access.mode,
            allow_download: access.allowDownload,
            do_index: access.doIndex,
            ...(access.password !== undefined ? { password: access.password } : {}),
            ...(access.expiresAt !== undefined ? { expires_at: access.expiresAt } : {}),
            ...(access.expiredUrl !== undefined ? { expired_url: access.expiredUrl } : {}),
          },
        },
      }),
      transformResponse: (body: unknown) => {
        const parsed = parseHostedPdf(body);
        if (!parsed) throw new Error('The hosted PDF update returned an invalid response.');
        return parsed;
      },
      invalidatesTags: ['HostedPdfs'],
    }),
    deleteHostedPdf: build.mutation<void, { namespace: string }>({
      query: ({ namespace }) => ({
        url: `/v1/host/${encodeURIComponent(namespace)}`,
        method: 'DELETE',
      }),
      transformResponse: () => undefined,
      invalidatesTags: ['HostedPdfs'],
    }),
  }),
});

export const {
  useGetHostedPdfAnalyticsQuery,
  useListHostedPdfsQuery,
  useListAllHostedPdfsQuery,
  useCreateHostedPdfMutation,
  useCheckHostedLinkQuery,
  useUpdateHostedPdfMutation,
  useDeleteHostedPdfMutation,
} = hostEndpoints;
